import { Component, ComponentType } from "@/types";
import { BaseCommandHandler, CommandResult, PropertyChange } from "../BaseCommandHandler";

const MIME_PRESETS: Record<string, string[]> = {
  images: ['image/*'],
  documents: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.*', 'text/*'],
  videos: ['video/*'],
  audio: ['audio/*'],
  archives: ['application/zip', 'application/x-tar', 'application/x-rar-compressed', 'application/gzip'],
  all: [],
};

function toBytes(value: number, unit: string): number {
  switch (unit.toLowerCase()) {
    case 'kb': return value * 1024;
    case 'mb': return value * 1024 * 1024;
    case 'gb': return value * 1024 * 1024 * 1024;
    default: return value;
  }
}

/**
 * Command Handler for File Storage components.
 *
 * 15 pattern groups covering:
 * - Display mode, title, empty state message
 * - File types (presets + custom), max file size, max files
 * - Enable/disable toggles (upload, delete, folder management, search, thumbnails, quota)
 * - Show/hide toggles (search, thumbnails, quota, file count, file size, upload date, uploader)
 * - Scope, visibility, encryption
 * - Sort by, sort direction
 */
export class FileStorageCommandHandler extends BaseCommandHandler {
  constructor() {
    super();
    this.initializePatterns();
  }

  private initializePatterns(): void {
    // 1. Display mode
    this.commandPatterns.set(
      /(?:set |change |switch (?:to )?)?(?:display )?(?:mode )?(?:to )?(grid|list)(?: (?:view|mode))?/i,
      (match) => ({ properties: { displayMode: match[1].toLowerCase() } })
    );

    // 2. File type presets: "allow only images/documents/videos/audio/archives"
    this.commandPatterns.set(
      /(?:allow |accept )?(?:only )?(images?|documents?|videos?|audio|archives?)/i,
      (match) => {
        const key = match[1].toLowerCase().replace(/s$/, '');
        const mapped = key === 'image' ? 'images'
          : key === 'document' ? 'documents'
          : key === 'video' ? 'videos'
          : key === 'archive' ? 'archives'
          : key;
        const types = MIME_PRESETS[mapped] || MIME_PRESETS[key] || [];
        return { properties: { allowedFileTypes: types } };
      }
    );

    // 3. Allow all file types
    this.commandPatterns.set(
      /allow all (?:file )?types?/i,
      () => ({ properties: { allowedFileTypes: [] } })
    );

    // 4. Custom file types: "restrict to .pdf, .docx" or "allow types: .pdf, .docx"
    this.commandPatterns.set(
      /(?:restrict|limit|set (?:file )?types?) (?:to |:?\s*)([.\w,\s/*]+)/i,
      (match) => ({
        properties: {
          allowedFileTypes: match[1].split(/[,\s]+/).map((t: string) => t.trim()).filter(Boolean),
        },
      })
    );

    // 5. Max file size: "set max size to 10 MB"
    this.commandPatterns.set(
      /(?:set |change )?(?:max(?:imum)? )?(?:file )?size (?:to |limit (?:to )?)?(\d+)\s*(kb|mb|gb)?/i,
      (match) => ({
        properties: { maxFileSize: toBytes(parseInt(match[1], 10), match[2] || 'mb') },
      })
    );

    // 6. Enable/disable toggles
    this.commandPatterns.set(
      /(enable|disable|allow|disallow|prevent)\s+(upload(?:s|ing)?|delet(?:e|ion|ing)|folder\s*management|search|thumbnails?|quota(?:\s*bar)?)/i,
      (match) => {
        const enabled = /^(enable|allow)$/i.test(match[1]);
        const target = match[2].toLowerCase().replace(/s$/, '').replace(/ing$/, '');
        const propMap: Record<string, string> = {
          upload: 'uploadEnabled',
          delet: 'deleteEnabled',
          delete: 'deleteEnabled',
          deletion: 'deleteEnabled',
          'folder management': 'folderManagementEnabled',
          'foldermanagement': 'folderManagementEnabled',
          search: 'showSearch',
          thumbnail: 'showThumbnails',
          quota: 'showQuotaBar',
          'quota bar': 'showQuotaBar',
          'quotabar': 'showQuotaBar',
        };
        const prop = propMap[target] || propMap[target.replace(/\s+/g, '')];
        if (!prop) return null;
        return { properties: { [prop]: enabled } };
      }
    );

    // 7. Title
    this.commandPatterns.set(
      /(?:set |change )?title (?:to )?["']?([^"']+?)["']?\s*$/i,
      (match) => ({ properties: { title: match[1].trim() } })
    );

    // 8. Scope
    this.commandPatterns.set(
      /(?:set |change )?(?:default )?scope (?:to )?(private|project|shared)/i,
      (match) => ({ properties: { defaultScope: match[1].toLowerCase() } })
    );

    // 9. Visibility
    this.commandPatterns.set(
      /(?:set |change )?(?:default )?visibility (?:to )?(private|org|public)/i,
      (match) => ({ properties: { defaultVisibility: match[1].toLowerCase() } })
    );

    // 10. Sort by
    this.commandPatterns.set(
      /sort (?:by )?(name|date|size|type)/i,
      (match) => ({ properties: { sortDefault: match[1].toLowerCase() } })
    );

    // 11. Sort direction
    this.commandPatterns.set(
      /sort(?:ing)?\s+(asc(?:ending)?|desc(?:ending)?)/i,
      (match) => ({
        properties: { sortDirection: match[1].toLowerCase().startsWith('asc') ? 'asc' : 'desc' },
      })
    );

    // 12. Max files
    this.commandPatterns.set(
      /(?:set |change )?(?:max(?:imum)? )?(?:number of )?files? (?:limit |count )?(?:to )?(\d+)/i,
      (match) => ({ properties: { maxFiles: parseInt(match[1], 10) } })
    );

    // 13. Empty state message
    this.commandPatterns.set(
      /(?:set |change )?(?:empty (?:state )?)?message (?:to )?["']?([^"']+?)["']?\s*$/i,
      (match) => ({ properties: { emptyStateMessage: match[1].trim() } })
    );

    // 14. Encryption
    this.commandPatterns.set(
      /(?:set |change )?encryption (?:to )?(none|org)/i,
      (match) => ({ properties: { encryptionDefault: match[1].toLowerCase() } })
    );

    // 15. Show/hide toggles
    this.commandPatterns.set(
      /(show|hide)\s+(search|thumbnails?|quota(?:\s*bar)?|file\s*count|file\s*size|upload\s*date|uploader)/i,
      (match) => {
        const visible = match[1].toLowerCase() === 'show';
        const noun = match[2].toLowerCase().replace(/\s+/g, '');
        const nounMap: Record<string, string> = {
          search: 'showSearch',
          thumbnail: 'showThumbnails',
          thumbnails: 'showThumbnails',
          quota: 'showQuotaBar',
          quotabar: 'showQuotaBar',
          filecount: 'showFileCount',
          filesize: 'showFileSize',
          uploaddate: 'showUploadDate',
          uploader: 'showUploader',
        };
        const prop = nounMap[noun];
        if (!prop) return null;
        return { properties: { [prop]: visible } };
      }
    );
  }

  canHandle(command: string, component: Component): boolean {
    return component.type === ComponentType.FILE_STORAGE && this.hasFileStorageKeywords(command);
  }

  private hasFileStorageKeywords(command: string): boolean {
    const keywords = [
      "grid", "list", "view", "mode", "display",
      "title", "rename",
      "file size", "max size", "size limit",
      "file type", "allow", "accept", "image", "document", "video", "pdf",
      "upload", "delete", "deletion",
      "search", "quota", "thumbnail",
      "folder", "scope",
      "sort", "ascending", "descending",
      "visibility", "encryption",
      "empty", "message",
      "max files", "file count", "file size",
      "upload date", "uploader",
      "folder management",
    ];
    const lowerCommand = command.toLowerCase();
    return keywords.some((keyword) => lowerCommand.includes(keyword));
  }

  async parseCommand(command: string, component: Component): Promise<CommandResult> {
    const commandParts = command.split(/\s+(?:and|with|also)\s+/i);
    const changes: PropertyChange = { properties: {}, style: {} };

    for (const part of commandParts) {
      const patternMatch = this.matchPatterns(part, component);
      if (patternMatch) {
        if (patternMatch.properties) {
          changes.properties = { ...changes.properties, ...patternMatch.properties };
        }
        if (patternMatch.style) {
          changes.style = { ...changes.style, ...patternMatch.style };
        }
      }
    }

    if (
      Object.keys(changes.properties || {}).length > 0 ||
      Object.keys(changes.style || {}).length > 0
    ) {
      const validation = this.validateChanges(changes, component);
      if (!validation.valid) {
        return {
          success: false,
          error: validation.error,
          suggestions: await this.getSuggestions(component),
        };
      }

      return {
        success: true,
        command: {
          type: this.determineCommandType(changes),
          changes,
          confidence: this.calculateConfidence(command, changes),
          description: this.generateDescription(changes, component),
        },
        suggestions: await this.getSuggestions(component),
      };
    }

    return {
      success: false,
      error: "Could not understand the file storage command",
      suggestions: await this.getSuggestions(component),
    };
  }

  async getExampleCommands(): Promise<string[]> {
    return [
      'set display to grid',
      'set display to list',
      'allow only images',
      'allow only documents',
      'allow all file types',
      'restrict to .pdf, .docx',
      'set max size to 10 MB',
      'enable upload',
      'disable delete',
      'enable folder management',
      'set title to Project Documents',
      'set scope to private',
      'set visibility to org',
      'sort by date',
      'sort descending',
      'set max files to 50',
      'set empty message to "Drop files here"',
      'set encryption to org',
      'show search',
      'hide quota',
      'show upload date',
      'hide uploader',
      'show file count',
    ];
  }

  async getSuggestions(component: Component): Promise<string[]> {
    const props = component.properties || {};
    const suggestions: string[] = [];

    if (props.displayMode === 'grid') {
      suggestions.push('set display to list');
    } else {
      suggestions.push('set display to grid');
    }

    suggestions.push('allow only images');
    suggestions.push('allow only documents');
    suggestions.push('set max size to 10MB');

    if (props.uploadEnabled === false) {
      suggestions.push('enable upload');
    } else {
      suggestions.push('disable upload');
    }

    if (props.deleteEnabled === false) {
      suggestions.push('enable delete');
    } else {
      suggestions.push('disable delete');
    }

    suggestions.push('set title to Project Documents');
    suggestions.push('sort by date');
    suggestions.push('show search');
    suggestions.push('hide quota');

    return suggestions;
  }
}

export const fileStorageCommandHandler = new FileStorageCommandHandler();
